import { DirtyCellMap } from 'store/useCapTableDirtyCellStore';
import { CaptableColumn } from '../components/captable-table/CaptableTable.props';

/**
 * Merge server-side securities into the local grid columns without losing
 * local-only state: unsaved cell edits (dirty registry), locally deleted
 * columns, and unsaved new columns. Saved columns missing from `incoming`
 * are dropped (a server-side delete wins). SCAL-8959.
 */
export const mergeSecuritiesWithLocal = (
  incoming: CaptableColumn[],
  prev: CaptableColumn[],
  dirtyBySecurityId: DirtyCellMap
): CaptableColumn[] => {
  const locallyDeletedIds = new Set(prev.filter(c => c.is_deleted).map(c => c.id));

  const merged = incoming.map(s => {
    // A locally deleted column's pending cell edits are moot: deleted wins.
    if (locallyDeletedIds.has(s.id)) {
      return { ...s, is_deleted: true } as CaptableColumn;
    }
    const dirty = s.id != null ? dirtyBySecurityId[s.id] : undefined;
    return dirty && Object.keys(dirty).length ? ({ ...s, ...dirty } as CaptableColumn) : s;
  });

  // Unsaved new columns only exist locally and have no persisted id yet. A
  // just-saved column comes back from the server under a real id but the same
  // client-assigned security_ref. A just-saved multi-row ledger column
  // (warrant/option) is expanded server-side into hidden child securities
  // whose security_group_ref is the parent column's ref, and the parent itself
  // is never persisted. Either way the id-0 local copy must not survive.
  const incomingRefs = new Set<string>();
  incoming.forEach(({ security_ref, security_group_ref }) => {
    if (security_ref) incomingRefs.add(security_ref);
    if (security_group_ref) incomingRefs.add(security_group_ref);
  });
  const unsavedLocal = prev.filter(
    c => !c.is_deleted && !c.id && !(c.security_ref && incomingRefs.has(c.security_ref))
  );

  // No local changes: hand back the original array so React can bail out of re-renders.
  if (unsavedLocal.length === 0 && merged.every((s, i) => s === incoming[i])) {
    return incoming;
  }

  return [...merged, ...unsavedLocal];
};
